import { useTranslation } from 'react-i18next';

import { usePlanHistory } from '../../../hooks/usePlanHistory';
import type { PaymentHistoryMode } from '../../../types/paymentHistory';
import { PaymentHistoryDrawer } from '../PaymentHistoryDrawer';

/** Props de `PlanHistoryDrawer`. */
export interface PlanHistoryDrawerProps {
  /** `null` mantém o drawer fechado. */
  readonly financialPersonId: number | null;
  readonly mode: PaymentHistoryMode | null;
  /** Aluno do contrato, exibido no subtítulo para situar o escopo. */
  readonly studentName?: string | null | undefined;
  readonly onClose: () => void;
}

/**
 * Painel lateral com o histórico de um plano financeiro.
 *
 * Atende as duas ações de leitura do cabeçalho do card do contrato na tela de
 * edição: "Rastreamento de pedidos"
 * (`GET /financial-people/{financialPersonId}/order-tracking`, eventos de
 * integração) e "Rastreamento de operações"
 * (`GET /financial-people/{financialPersonId}/traceability`, auditoria campo a
 * campo do plano). Os rótulos vêm do legado e não espelham os nomes dos endpoints.
 *
 * A apresentação vive em `PaymentHistoryDrawer`, compartilhada com o histórico da
 * parcela: aqui ficam apenas o escopo (o hook do plano) e os textos. As duas
 * leituras não tocam o editor, então abrir e fechar o painel preserva o formulário.
 */
export function PlanHistoryDrawer({
  financialPersonId,
  mode,
  studentName,
  onClose,
}: PlanHistoryDrawerProps) {
  const { t } = useTranslation('individualPayment');
  const history = usePlanHistory(financialPersonId, mode);

  return (
    <PaymentHistoryDrawer
      open={financialPersonId !== null && mode !== null}
      mode={mode}
      history={history}
      title={
        mode === 'orderTracking'
          ? t('planEdit.headerActions.orderTracking')
          : t('planEdit.headerActions.operationsTracking')
      }
      subtitle={studentName ?? undefined}
      emptyHint={
        mode === 'orderTracking'
          ? t('planEdit.history.emptyOrderTrackingHint')
          : t('planEdit.history.emptyOperationsHint')
      }
      onClose={onClose}
    />
  );
}
